import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, CircularProgress, Alert, FormControl, InputLabel, Select,
    MenuItem, Chip, LinearProgress
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { axios_tenant } from "../../../../axios";
import Button from "../../../shared/OAWorkflow/components/atoms/Button";
import { BRAND_COLORS } from "../../../shared/OAWorkflow/constants/taskStatuses";
import { useDocumentTitle } from "../../../shared/OAWorkflow/utils/useDocumentTitle";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import "./OAWorkflow.css";

const BASE_URL = "/collaboration/workflow";
const ACCEPTED_EXTENSIONS = ".xlsx,.xls,.csv,.msg";
const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 25;

/**
 * PGS File Upload — Admin/AM drag-and-drop multi-file upload page
 * for Proofreading Reports, Granted Matters, and .msg/.pdf files.
 */
const PGSIntakeUpload = () => {
    useDocumentTitle("Post Grant Support — Upload");
    const navigate = useNavigate();
    const outerTheme = useTheme();
    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: { fontFamily: 'Lato, sans-serif' },
    }), [outerTheme]);

    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [clientCode, setClientCode] = useState("");
    const [clients, setClients] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [dragOver, setDragOver] = useState(false);

    // Fetch client list
    useEffect(() => {
        axios_tenant.get(`${BASE_URL}/intake/clients`, { params: { workType: "Post Grant Support" }, silent: true })
            .then(res => setClients(res.data.data || []))
            .catch(err => console.error("Failed to load clients:", err));
    }, []);

    const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
    const handleDragLeave = () => setDragOver(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        addFiles(Array.from(e.dataTransfer.files));
    };

    const addFiles = (newFiles) => {
        setErrorMessage("");
        setResult(null);

        const validFiles = newFiles.filter(f => {
            const ext = f.name.split(".").pop().toLowerCase();
            const validExts = ["xlsx", "xls", "csv", "msg"];
            if (!validExts.includes(ext)) {
                setErrorMessage(`Unsupported file type: .${ext}`);
                return false;
            }
            if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setErrorMessage(`File too large: ${f.name} (max ${MAX_FILE_SIZE_MB}MB)`);
                return false;
            }
            return true;
        });

        setFiles(prev => {
            const combined = [...prev, ...validFiles];
            if (combined.length > MAX_FILES) {
                setErrorMessage(`Maximum ${MAX_FILES} files allowed`);
                return combined.slice(0, MAX_FILES);
            }
            return combined;
        });
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setErrorMessage("Please select files to upload");
            return;
        }
        if (!clientCode) {
            setErrorMessage("Please select a client");
            return;
        }

        setUploading(true);
        setErrorMessage("");
        setResult(null);

        const formData = new FormData();
        files.forEach(f => formData.append("files", f));
        formData.append("clientCode", clientCode);

        try {
            const res = await axios_tenant.post(`${BASE_URL}/intake/pgs/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                silent: true
            });
            setResult(res.data.data);
            setFiles([]);
        } catch (err) {
            const msg = err?.response?.data?.message || "Upload failed. Please try again.";
            setErrorMessage(msg);
        } finally {
            setUploading(false);
        }
    };

    const formatSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <ThemeProvider theme={localTheme}>
            <Box className="oa-lato-font" sx={{ pt: 1, px: 4, pb: 10, maxWidth: 900, mx: "auto" }}
                data-testid="pgs-intake-upload-page">
                
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h5" sx={{ color: "#1E293B", fontSize: "22px", fontWeight: 500 }}>
                        Post Grant Support — Upload
                    </Typography>
                    <Button
                        variant="secondary"
                        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                        onClick={() => navigate("../tasks")}
                        sx={{ height: 36 }}
                    >
                        Back to Tasks
                    </Button>
                </Box>

                {/* Client Selector */}
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel>Select Client</InputLabel>
                    <Select
                        value={clientCode}
                        label="Select Client"
                        onChange={(e) => { setClientCode(e.target.value); setErrorMessage(""); }}
                        sx={{ backgroundColor: "#fff" }}
                    >
                        {clients.map(c => (
                            <MenuItem key={c.tenantCode || c.code} value={c.tenantCode || c.code}>
                                {c.tenantName ? `${c.tenantName} (${c.tenantCode || c.code})` : (c.clientName || c.name || c.tenantCode || c.code)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Drop Zone */}
                <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                        border: `2px dashed ${dragOver ? BRAND_COLORS.PRIMARY : "#CBD5E1"}`,
                        borderRadius: "12px",
                        backgroundColor: dragOver ? "#EAF2FF" : "#F8FAFC",
                        p: 5,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        mb: 3,
                        "&:hover": {
                            borderColor: BRAND_COLORS.PRIMARY,
                            backgroundColor: "#F2F7FC"
                        }
                    }}
                >
                    <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: BRAND_COLORS.PRIMARY, mb: 1 }} />
                    <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#334155", mb: 0.5 }}>
                        Drag & drop files here, or click to browse
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: "#94A3B8" }}>
                        Supports .xlsx, .xls, .csv, .msg — up to {MAX_FILES} files, {MAX_FILE_SIZE_MB}MB each
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ACCEPTED_EXTENSIONS}
                        onChange={(e) => addFiles(Array.from(e.target.files))}
                        style={{ display: "none" }}
                    />
                </Box>

                {/* File List */}
                {files.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#475569", mb: 1 }}>
                            {files.length} file{files.length !== 1 ? "s" : ""} selected
                        </Typography>
                        {files.map((f, idx) => (
                            <Box key={idx} sx={{
                                display: "flex", alignItems: "center", gap: 1.5,
                                backgroundColor: "#fff", border: "1px solid #E2E8F0",
                                borderRadius: "8px", p: 1.5, mb: 1
                            }}>
                                <DescriptionOutlinedIcon sx={{ color: "#64748B", fontSize: 20 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B" }}>
                                        {f.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                                        {formatSize(f.size)}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={f.name.split(".").pop().toUpperCase()}
                                    size="small"
                                    sx={{
                                        height: 22, fontSize: "10px", fontWeight: 600,
                                        bgcolor: "#F1F5F9", color: "#475569"
                                    }}
                                />
                                <CloseIcon
                                    sx={{ fontSize: 18, color: "#94A3B8", cursor: "pointer", "&:hover": { color: "#EF4444" } }}
                                    onClick={() => removeFile(idx)}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Upload Button */}
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={uploading || files.length === 0 || !clientCode}
                    sx={{ width: "100%", height: 44, fontSize: "15px", fontWeight: 500, mb: 3 }}
                >
                    {uploading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={18} color="inherit" />
                            Processing...
                        </Box>
                    ) : (
                        `Upload ${files.length > 0 ? `${files.length} file${files.length !== 1 ? "s" : ""}` : ""}`
                    )}
                </Button>

                {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

                {/* Error */}
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                        {errorMessage}
                    </Alert>
                )}

                {/* Results */}
                {result && (
                    <Box sx={{
                        backgroundColor: "#fff", border: "1px solid #E2E8F0",
                        borderRadius: "12px", p: 3
                    }}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            Upload Results
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                            <Chip
                                icon={<CheckCircleOutlineIcon />}
                                label={`${result.summary?.totalTasksCreated || 0} tasks created`}
                                color="success" variant="outlined" size="small"
                            />
                            <Chip
                                label={`${result.summary?.successFiles || 0}/${result.summary?.totalFiles || 0} files processed`}
                                color="info" variant="outlined" size="small"
                            />
                            {result.summary?.failedFiles > 0 && (
                                <Chip
                                    icon={<ErrorOutlineIcon />}
                                    label={`${result.summary.failedFiles} failed`}
                                    color="error" variant="outlined" size="small"
                                />
                            )}
                        </Box>

                        {/* Per-file details */}
                        <Box sx={{ mb: 2 }}>
                            {result.files?.map((f, idx) => (
                                <Box key={idx} sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    py: 1.5, borderTop: idx > 0 ? "1px solid #F1F5F9" : "none"
                                }}>
                                    {f.status === "success" ? (
                                        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#16A34A" }} />
                                    ) : (
                                        <ErrorOutlineIcon sx={{ fontSize: 18, color: "#EF4444" }} />
                                    )}
                                    <Typography sx={{ fontSize: "13px", color: "#334155", flex: 1 }}>
                                        {f.fileName}
                                    </Typography>
                                    <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                        {f.created > 0 ? `${f.created} created` : (f.reason || "No tasks")}
                                        {f.duplicates > 0 ? `, ${f.duplicates} duplicates` : ""}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <Button
                            variant="primary"
                            onClick={() => navigate("../validation-results")}
                            sx={{ width: "100%", height: 40, mt: 1 }}
                        >
                            OK
                        </Button>
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default PGSIntakeUpload;
